import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import convert from "convert-units";

import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import AddExerciseModal from "../../../parts/modals/add-exercise";
import EditProgramSettingsModal from "../../../parts/modals/edit-program-settings";
import EditorSessionTableRow from "./components/EditorSessionTableRow";
import Chart from "../../../parts/chart";
import Select from "../../../parts/inputs/select";
import ToolTip from "../../../parts/tooltip";

import {
  validateSet,
  checkOtherExercise,
} from "../../../utils/exercise-validations";

// Constants
const {
  ZONES,
  PRIMARIES,
  DATE_FORMATS: { DATE_DAY_FORMAT },
} = require("../../../constants");

const ZONES_LEGEND = _.map(ZONES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

const PRIMARIES_LEGEND = _.map(PRIMARIES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

class View extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      openQuickEdit: false,
      exercises: [],
    };
    this.loadInputs = [];
    this.intensityInputs = [];
  }

  handleDuplicateExercise(body) {
    submit("/actions/exercise/duplicate", {
      method: "POST",
      body,
    });
  }

  initDND() {
    if (
      !this.refs.exercises ||
      _.get(this.refs, "exercises.children", []).length < 2
    )
      return;
    const dragula = require("react-dragula");
    this.exercises = dragula([this.refs.exercises], {
      moves: (el, container, handle) => handle.classList.contains("sort"),
    }).on("drop", handleDrop);

    // Store original reactids so that we can ensure they are consistent.
    const originalIDs = _.map(_.get(this.refs, "exercises.children"), (el) =>
      el.getAttribute("data-reactid")
    );
    function handleDrop(el, target) {
      // Submit a form to update the order in the backend.
      submit("/actions/exercise/order", {
        method: "POST",
        body: {
          exercises: _.map(target.children, (el) => el.getAttribute("data-id")),
        },
      });
    }
  }

  stopDND() {
    this.exercises && this.exercises.destroy();
  }

  componentDidMount() {
    this.initDND();
  }
  componentDidUpdate() {
    this.stopDND();
    this.initDND();
  }
  componentWillUnmount() {
    this.stopDND();
  }

  openQuickEditMode(e) {
    e.preventDefault();
    this.setState((prevState) =>
      Object.assign({}, prevState, { openQuickEdit: true })
    );
  }

  closeQuickEditMode(e) {
    e && e.preventDefault();
    document.getElementById("quick-edit").reset();
    this.setState((prevState) =>
      Object.assign({}, prevState, { openQuickEdit: false })
    );
    submit("/actions/exercise/quick-edit/cancel");
  }

  handleQuickEditChange({ target: { name, value } }, exId, setId) {
    const { locals } = this.context;
    const exercises = _.cloneDeep(this.state.exercises);
    const exerciseIndex = exercises.findIndex((ex) => ex._id == exId);
    const exercise =
      exercises[exerciseIndex] ||
      _.cloneDeep(_.get(this.context.locals, "editExercises", [])).find(
        (ex) => ex._id == exId
      ) ||
      _.cloneDeep(_.get(this.context.locals, "activeExercises", [])).find(
        (ex) => ex._id == exId
      ) ||
      {};

    const set = _.get(exercise, ["sets", setId], false);

    if (set && set[name] !== value && name !== "exerciseType") {
      set[name] = value;
      const primary = _.get(exercise, "primary");
      const repMax =
        exercise.type != "secondary"
          ? _.get(
              exercise,
              `repMaximums.${primary}`,
              _.get(this.context.locals.activeUser, `repMaximums.${primary}`, 0)
            )
          : exercise.secondaryRepMaximum;

      // ensure load and percentRepMax are in sync
      if (name === "load") {
        if (this.context.locals.activeUser.units === "imperial")
          set.load = _.round(convert(value).from("lb").to("kg"), 2);
        if (exercise.type !== "assistant")
          set.percentRepMax = _.round((set.load / repMax) * 100, 1);
      }

      if (name === "percentRepMax")
        set.load = _.round((repMax * set.percentRepMax) / 100, 2);

      const loadInput = this.loadInputs[`${exId}_${setId}`];
      if (loadInput) {
        if (name === "percentRepMax") {
          let loadToShow = set.load;
          if (this.context.locals.activeUser.units === "imperial") {
            loadToShow = _.round(convert(loadToShow).from("kg").to("lb"), 2);
          }
          loadInput.value = loadToShow;
        }
        if (!this.intensityInputs[`${exId}_${setId}`]) {
          this.intensityInputs[`${exId}_${setId}`] = { value: 0 };
        }
        this.intensityInputs[`${exId}_${setId}`].value = set.percentRepMax;
      }

      const validatedSet = validateSet({
        exercise,
        set,
        locals,
        field: name,
      });
      if (validatedSet) {
        exercise.sets[setId] = validatedSet;
        this.setExersicesStates(exercise, exercises, exerciseIndex);
      } else {
        // revert to original value
        document.getElementById(`${exId}_${setId}_${name}`).value = _.cloneDeep(
          _.get(this.context.locals, "editExercises", [])
        ).find((ex) => ex._id == exId).sets[setId][name];
      }
    } else if (name === "notes") {
      exercise.notes = value === "" ? " " : value;

      this.setExersicesStates(exercise, exercises, exerciseIndex);
    } else if (name === "exerciseType") {
      const { movement, repMaximum, newPrimary } = value;
      const sets = _.get(exercise, "sets", false);

      // if the movement chosen is a primary, change to a primary
      if (
        movement === "squat" ||
        movement === "bench" ||
        movement === "deadlift"
      ) {
        // setting new values
        exercise.type = "primary";
        exercise.primary = movement;
        // resetting secondary and secondary RM
        exercise.secondary = "null";
        exercise.secondaryRepMaximum = 0;
        // updating the values of each set in the exercise
        sets.map((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = _.round(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = _.round(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = _.round(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
        });
      }
      // if repMaximum is undefined and isAssistant = true then it is assistant
      else if (!repMaximum && newPrimary === "assistant") {
        exercise.type = "assistant";
        exercise.primary = "assistant";
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = 0;
      }
      // else its a secondary
      else {
        // change type from primary/assistant to secondary and set new values
        exercise.type = "secondary";
        exercise.primary = newPrimary;
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = repMaximum;
        // updating the values of each set in the exercise
        sets.map((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = _.round(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = _.round(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = _.round(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
        });
      }

      this.setExersicesStates(exercise, exercises, exerciseIndex);
    }
  }

  setExersicesStates(exercise, exercises, exerciseIndex) {
    // if exercise already exists in state
    if (exerciseIndex > -1) exercises[exerciseIndex] = exercise;
    // otherwise push to exercises
    if (exerciseIndex < 0) exercises.push(exercise);

    this.setState((prevState) => Object.assign({}, prevState, { exercises }));
  }

  saveQuickEdit(e) {
    e.preventDefault();
    const { exercises } = this.state;

    if (exercises === undefined || exercises.length === 0) {
      this.closeQuickEditMode();
    } else if (checkOtherExercise(this.state.exercises)) {
      submit("/actions/exercise/quick-edit", {
        method: "POST",
        body: { exercises },
      });
    }

    this.closeQuickEditMode();
  }

  render() {
    const { locals } = this.context;

    const {
      query,
      editExercises,
      activeUser,
      editCycle,
      exercisesVolumeForCycle,
      exercisesLiftsForCycle,
      exercisesVolumeGroupZonesForCycle,
      fromMetric,
    } = locals;
    const program = _.find(editCycle.programs, { order: query.program });

    const session = _.find(program.sessions, {
      order: query.session,
      week: query.week,
    });
    const sessionsGroupedByOrder = _.groupBy(program.sessions, "order");
    const maxOrder = _.maxBy(Object.keys(sessionsGroupedByOrder), (order) =>
      parseInt(order)
    );

    const sessionsGroupedByWeek = _.groupBy(program.sessions, "week");
    const maxWeek = _.maxBy(Object.keys(sessionsGroupedByWeek), (week) =>
      parseInt(week)
    );
    const { units } = query;

    const activeDay = moment(query.day, DATE_DAY_FORMAT);
    const selectDayRange = _.map(_.range(0, 7), (i) =>
      activeDay.clone().add(i, "days")
    );
    const metricDayRange =
      query.period === "day" ? [activeDay, activeDay] : selectDayRange;

    const totalLifts = _.sumBy(
      _.values(exercisesLiftsForCycle),
      (v) => _.values(v)[0]
    );
    const averageItensity = 0;

    const seriesVolume = _(PRIMARIES)
      .map((name) => {
        return {
          name,
          className: name,
          data: [
            locals.fromMetric(
              _.get(exercisesVolumeForCycle, [name, query.week, "volume"]),
              units
            ),
          ],
        };
      })
      .value();

    const seriesVolumeSplit = _(PRIMARIES)
      .map((name) => {
        return {
          name,
          className: name,
          value: locals.fromMetric(
            _.get(exercisesVolumeForCycle, [name, query.week, "volume"]),
            units
          ),
        };
      })
      .filter("value")
      .value();

    //TODO; come back and fix this to remove the duplication
    const seriesVolumeIntensitySplit = _(PRIMARIES)
      .map((name) => {
        return {
          name,
          className: name,
          value: _.get(exercisesVolumeForCycle, [name, query.week, "volume"]),
        };
      })
      .filter("value")
      .value();

    const totalVolume = _.sumBy(seriesVolumeSplit, "value");
    const totalIntensityVolume = _.sumBy(seriesVolumeIntensitySplit, "value");
    const totalIntensity = _.reduce(
      exercisesVolumeForCycle,
      (prev, metric) =>
        _.get(metric, query.week, false)
          ? prev +
            metric[query.week].average *
              (metric[query.week].volume / totalIntensityVolume)
          : prev,
      0
    );
    const selectedZonesMetrics =
      _.get(query, "zoneType", "total") === "total"
        ? exercisesVolumeGroupZonesForCycle
        : _.pick(exercisesVolumeGroupZonesForCycle, query.zoneType);
    const totalZoneVolume = locals.fromMetric(
      _(selectedZonesMetrics).flatMap(_.values).sum(),
      units
    );

    const seriesZones = _(ZONES)
      .map((zone) => {
        return {
          name: zone,
          className: zone,
          value:
            locals.fromMetric(
              _.sumBy(_.values(selectedZonesMetrics), zone) || 0,
              units
            ) / totalZoneVolume,
        };
      })
      .filter("value")
      .value();

    if (_.isEmpty(seriesZones)) {
      seriesZones.push({
        className: "empty",
        name: "empty",
        value: 1,
      });
    }

    const fullExercisesOfSession = [];
    session.exercises.map((ex) => {
      const fullEx = locals.editExercises.find((fe) => fe._id === ex);
      fullExercisesOfSession.push(fullEx);
    });

    return (
      <div className="Program-Editor">
        <div className="Sub-Header">
          <div className="inset">
            <div className="program-name">{program.name}</div>
            <div className="settings">
              <img src="/img/icons/settings.svg" alt="settings" />
              <a href={`/actions/refresh?modal[type]=edit-program-settings`}>
                {" "}
                SETTINGS
              </a>
            </div>
            <div className="cycle-name">{editCycle.name}</div>
            <a
              className="button"
              href={`/dashboard/program-builder/edit-program/${editCycle._id}?program=${query.program}`}
            >
              Back
            </a>
          </div>
        </div>

        <div className="Main">
          <div className="Daily-Workout">
            <div className="heading">
              WEEK {query.week} {">"} WORKOUT {query.session}
            </div>
            <div className="selector">
              <div className="selector-group">
                <a
                  className={`prev ${
                    parseInt(query.session) == 1 ? "disabled" : ""
                  }`}
                  href={`/dashboard/program-builder/workout?cycle=${
                    editCycle._id
                  }&program=${query.program}&session=${
                    parseInt(query.session) - 1
                  }&week=${query.week}`}
                >
                  <img src="/img/svg/icon_arrow-up.svg" />
                </a>

                <a
                  className={`next ${
                    parseInt(query.session) == maxOrder ? "disabled" : ""
                  }`}
                  href={`/dashboard/program-builder/workout?cycle=${
                    editCycle._id
                  }&program=${query.program}&session=${
                    parseInt(query.session) + 1
                  }&week=${query.week}`}
                >
                  <img src="/img/svg/icon_arrow-down.svg" />
                </a>

                <Select
                  className="select-input"
                  value={query.session}
                  name="session"
                >
                  {_.map(sessionsGroupedByOrder, (sessions, order) => (
                    <option key={order} value={order}>
                      Workout {order}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="selector-group">
                <a
                  className={`prev ${
                    parseInt(query.week) == 1 ? "disabled" : ""
                  }`}
                  href={`/dashboard/program-builder/workout?cycle=${
                    editCycle._id
                  }&program=${query.program}&session=${query.session}&week=${
                    parseInt(query.week) - 1
                  }`}
                >
                  <img src="/img/svg/icon_arrow-left.svg" />
                </a>

                <a
                  className={`next ${
                    parseInt(query.week) == maxWeek ? "disabled" : ""
                  }`}
                  href={`/dashboard/program-builder/workout?cycle=${
                    editCycle._id
                  }&program=${query.program}&session=${query.session}&week=${
                    parseInt(query.week) + 1
                  }`}
                >
                  <img src="/img/svg/icon_arrow-right.svg" />
                </a>

                <Select className="select-input" value={query.week} name="week">
                  {_.map(sessionsGroupedByWeek, (sessions, week) => (
                    <option key={week} value={week}>
                      Week {week}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            {session ? (
              <div
                className={`exercises-frame ${
                  this.state.openQuickEdit && "quick-edit"
                }`}
              >
                <div className="exercises-head">
                  <div className="exercises-name">Exercise</div>
                  <div className="exercises-columns"></div>
                </div>
                <form id="quick-edit">
                  <ul className="exercises-list" ref="exercises">
                    {_.map(session.exercises, (exercise, i) => {
                      return (
                        <EditorSessionTableRow
                          exercises={fullExercisesOfSession}
                          exercise={exercise}
                          exerciseOrder={i}
                          fromMetric={fromMetric}
                          activeUser={activeUser}
                          cycle={editCycle}
                          program={program}
                          programOrder={program.order}
                          session={session}
                          handleDuplicate={(exercise) =>
                            this.handleDuplicateExercise(exercise)
                          }
                          isProgramEditor={false}
                          openQuickEditMode={(e) => this.openQuickEditMode(e)}
                          handleQuickEditChange={(e, exId, setId) =>
                            this.handleQuickEditChange(e, exId, setId)
                          }
                          loadInputs={this.loadInputs}
                          intensityInputs={this.intensityInputs}
                          isQuickEditOpen={this.state.openQuickEdit}
                        />
                      );
                    })}
                  </ul>
                  <input type="radio" name="more" id="more-reset" />
                </form>
                <div className="exercises-foot">
                  <div className="default-foot">
                    <a
                      className="add"
                      href={`/actions/refresh?modal[type]=add-exercise&week=${session.week}&session=${session.order}&editMode=false`}
                    >
                      Add Exercise
                    </a>
                    <ToolTip id="quick-edit-workout" body="Quick Edit">
                      <a
                        onClick={(e) => this.openQuickEditMode(e)}
                        className="quick-edit"
                        href="#_"
                      >
                        <img src="/img/icons/edit.svg" alt="edit" />
                      </a>
                    </ToolTip>
                  </div>
                  <div className="quick-edit-foot">
                    <a
                      onClick={(e) => this.saveQuickEdit(e)}
                      className={`button ${
                        !this.state.exercises.length &&
                        !this.context.locals.query.edit &&
                        "alt-disabled"
                      }`}
                      href="#_"
                    >
                      Save
                    </a>
                    <a
                      onClick={(e) => this.closeQuickEditMode(e)}
                      className="text-button sm"
                      href="#"
                    >
                      Close
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <h2>
                This workout does not exist, go back to the program editor to
                add it in.
              </h2>
            )}
          </div>

          {session && (
            <div key={query.day} className="section Quick-Metrics">
              <h4 className="alt">Quick Metrics</h4>

              <br />

              <div className="columns">
                <div className="column" data-col="1/3">
                  <div
                    className="Chart-Frame bar-chart"
                    data-chart="total-volume"
                  >
                    <div className="head">
                      <div className="right-side">
                        <div className="group">
                          <ToggleLinksInput
                            name="units"
                            options={[
                              { text: "kg", value: "metric" },
                              { text: "lbs", value: "imperial" },
                            ]}
                          />
                        </div>
                      </div>

                      <div className="left-side">
                        <div className="group">
                          <b>
                            Total Volume:{" "}
                            {locals.withWeight(_.round(totalVolume), units)}
                          </b>
                        </div>
                      </div>
                    </div>

                    <div className="body">
                      {!_.isEmpty(exercisesVolumeForCycle) && (
                        <div>
                          <div className="legend">{PRIMARIES_LEGEND}</div>
                          <div className="chart">
                            <Chart
                              type="Bar"
                              data={{ series: seriesVolume }}
                              options={{
                                seriesBarDistance: 40,
                                axisX: { showGrid: false },
                                axisY: { onlyInteger: true },
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="column" data-col="1/3">
                  <div className="Chart-Frame custom-data">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">
                          <b>Weighted Average Intensity</b>
                        </div>
                      </div>
                    </div>

                    <div className="body">
                      {!_.isEmpty(exercisesVolumeForCycle) && (
                        <div className="chart">
                          <table>
                            <tbody>
                              {_.map(PRIMARIES, (name) => {
                                const average = _.get(
                                  exercisesVolumeForCycle,
                                  [name, query.week, "average"],
                                  0
                                );
                                const intensity = (average * 100).toFixed(1);
                                return (
                                  <tr key={name}>
                                    <td>{_.capitalize(name)}</td>
                                    <td className={name}>{intensity}%</td>
                                  </tr>
                                );
                              })}
                              <tr>
                                <td colSpan="2" />
                              </tr>
                              <tr>
                                <td>Total</td>
                                <td className="total">
                                  {(totalIntensity * 100).toFixed(1)}%
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="column" data-col="1/3">
                  <div className="Chart-Frame custom-data">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">
                          <b>Number of Lifts</b>
                        </div>
                      </div>
                    </div>

                    <div className="body">
                      {!_.isEmpty(exercisesVolumeForCycle) && (
                        <div className="chart">
                          <table>
                            <tbody>
                              {_.map(PRIMARIES, (name) => {
                                const lifts = _.values(
                                  _.get(exercisesLiftsForCycle, name, { 0: 0 })
                                )[0];
                                return (
                                  <tr key={name}>
                                    <td>{_.capitalize(name)}</td>
                                    <td className={name}>{lifts}</td>
                                  </tr>
                                );
                              })}
                              <tr>
                                <td colSpan="2" />
                              </tr>
                              <tr>
                                <td>Total</td>
                                <td className="total">{totalLifts}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="column" data-col="1/2">
                  <div className="Chart-Frame pie-chart">
                    <div className="head">
                      <div className="right-side">
                        <div className="group">
                          <ToggleLinksInput
                            className="tabs"
                            name="zoneType"
                            options={[
                              { text: "Total", value: "total" },
                              { text: "Squat", value: "squat" },
                              { text: "Bench", value: "bench" },
                              { text: "Deadlift", value: "deadlift" },
                            ]}
                          />
                        </div>
                      </div>

                      <div className="left-side">
                        <div className="group">
                          <b>Zone of Relative Intensity</b>
                        </div>
                      </div>
                    </div>

                    <div className="body">
                      {!_.isEmpty(exercisesVolumeForCycle) && (
                        <div>
                          <div className="legend">{ZONES_LEGEND}</div>
                          <div className="chart" data-ratio="16:9">
                            <Chart
                              type="Pie"
                              data={{ series: seriesZones }}
                              options={{
                                labelInterpolationFnc: (val, i) => {
                                  if (
                                    _.get(seriesZones, [i, "name"]) === "empty"
                                  )
                                    return "";
                                  return `${Math.round(val * 100)}%`;
                                },
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="column" data-col="1/2">
                  <div className="Chart-Frame pie-chart">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">
                          <b>Volume Split by Core Lifts</b>
                        </div>
                      </div>
                    </div>

                    <div className="body">
                      {!_.isEmpty(exercisesVolumeForCycle) && (
                        <div>
                          <div className="legend">{PRIMARIES_LEGEND}</div>
                          <div className="chart" data-ratio="16:9">
                            <Chart
                              type="Pie"
                              data={{ series: seriesVolumeSplit }}
                              options={{
                                labelInterpolationFnc: (val) =>
                                  val &&
                                  ((val / totalVolume) * 100).toFixed(0) + "%",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const Workout = (app) => {
  app.get("/workout", async ({ req, res, locals, load }) => {
    const { query } = req;
    const {
      activeUser: {
        exerciseLibrary: { assistant },
      },
    } = locals;

    await Promise.all([
      load("editExercises", { cycle: query.cycle }),
      load("editCycle", { cycle: query.cycle }),
      load("exercisesVolumeForCycle", {
        id: query.cycle,
        program: query.program,
        week: query.week,
        session: query.session,
      }),
      load("exercisesLiftsForCycle", {
        id: query.cycle,
        program: query.program,
        week: query.week,
        session: query.session,
      }),
      load("exercisesVolumeGroupZonesForCycle", {
        id: query.cycle,
        program: query.program,
        week: query.week,
        session: query.session,
      }),
    ]);

    if (!query.session) return res.refreshWith({}, { url: "back" });
    const editExercise = query.editMode
      ? _.find(locals.editExercises, { _id: query.exercise })
      : {};
    locals.modal = {
      "add-exercise": (
        <AddExerciseModal
          editExercise={editExercise}
          lengthAssistantLibrary={assistant.length}
          secondaryAdded={query.secondaryAdded}
        />
      ),
      "edit-program-settings": <EditProgramSettingsModal />,
    }[_.get(query, "modal.type")];

    res.body = <View />;
  });
};

export default Workout;
